import React from 'react'
import {
  Divider,
  LeftHandNavigation,
  LeftHandNavigationHeader,
  LinkList,
  LinkListHeading,
  ListItem,
  Link,
  Banner,
  BoundedContent
} from '@vp/swan'

import StandardHeroContent from '../StandardHeroContent/StandardHeroContent'
import { useAppContext } from '../../state/AppContext'
import { getLink } from '../../utils/sideBarLinks/sidebarLinks'
import PageHeaderWithoutImage from './PageHeaderWithoutImage'
import { trackClickEvent } from '../../utils/tracking/tracking'
import {
  HeroContent,
  NewBanner,
  SidebarGroup,
  SidebarLink,
  SidebarType
} from '../../types/TemplateConfiguration'
import { renderSidebarDebugError, SidebarLinkError } from '../errorComponents/SidebarLinkError'
import classNames from 'classnames'
import CustomBannerWrapper from '../Banner'
import { MARKETING_COLORS } from '@vp/marketing-colors'
import { BFFPageData } from '../../services/serviceClients/BffClient'
import { DebugPageSections } from '../debugComponents/DebugPageSections'

interface PageHeaderProps {
  headerType: 'page header' | 'sub-category page header' | 'section header'
  configuration: HeroContent | NewBanner
  pageDataFromBFF: BFFPageData
  sidebar?: SidebarType
  isCarousel?: boolean
  index?: number
  marginBottom?: Record<string, number> | number
}

const PageHeader = (props: PageHeaderProps) => {
  const { headerType, configuration, sidebar, pageDataFromBFF, isCarousel, index, marginBottom } =
    props
  const { pageUrls, pageAvailabilities, mpvs } = pageDataFromBFF
  const { sidebarEnabled, merchandisingCategoryId, backgroundColor } = useAppContext()
  const newBanner = (configuration as NewBanner).banner

  const configurationAsHeroContent = configuration as HeroContent
  const fullWidthImage = !!configurationAsHeroContent.fullWidthImage
  const imageFocalPoint = configurationAsHeroContent?.imageFocalPoint
  const isH1 = shouldBeH1(headerType, isCarousel, index)
  const finalizedBackgroundColor = getBackgroundColor({
    defaultColor: backgroundColor,
    heroType: headerType,
    overrideColor: configurationAsHeroContent.backgroundColor?.name
  })
  if (!newBanner && !configurationAsHeroContent?.image && !sidebarEnabled) {
    if (configurationAsHeroContent?.title) {
      return (
        <>
          <PageHeaderWithoutImage type={headerType} {...configurationAsHeroContent} />
          <DebugPageSections
            rawSectionData={configuration}
            resolvedSectionData={configurationAsHeroContent}
          />
        </>
      )
    } else {
      return <></>
    }
  }

  const color = MARKETING_COLORS[finalizedBackgroundColor]
  const leftNav =
    sidebarEnabled && sidebar ? (
      <LeftHandNavigation>
        <LeftHandNavigationHeader>{sidebar?.categoryName?.value}</LeftHandNavigationHeader>
        {sidebar?.groups.map(
          (group: SidebarGroup, groupIndex: number) =>
            group.groupName && (
              <div key={group.groupName.value}>
                <Divider />
                <LinkListHeading>{group.groupName.value}</LinkListHeading>
                <LinkList>
                  {group.links.map((link: SidebarLink, linkIndex: number) => {
                    const { url, text, associatedPageAvailable } = getLink(
                      link,
                      mpvs,
                      pageUrls,
                      pageAvailabilities
                    )

                    if (renderSidebarDebugError(associatedPageAvailable)) {
                      return <SidebarLinkError url={url} text={text} key={linkIndex} />
                    }

                    return (
                      url &&
                      text &&
                      associatedPageAvailable && (
                        <ListItem key={url}>
                          <Link
                            href={url}
                            onClick={() =>
                              trackClickEvent({
                                categoryId: merchandisingCategoryId,
                                destinationUrl: url,
                                ctaValue: `Group ${groupIndex + 1} Link ${linkIndex + 1}`,
                                pageZone: 'Side Bar',
                                linkType: 'Text Layout Link'
                              })
                            }
                            data-section="Side Bar"
                            data-translation={
                              link.mpvId
                                ? link.mpvId
                                : `Group ${groupIndex + 1} Link ${linkIndex + 1}`
                            }
                          >
                            {text}
                          </Link>
                        </ListItem>
                      )
                    )
                  })}
                </LinkList>
              </div>
            )
        )}
      </LeftHandNavigation>
    ) : undefined

  if (newBanner) {
    if (newBanner.bannerTemplate) {
      return (
        <>
          <CustomBannerWrapper
            bannerProps={newBanner.bannerTemplate}
            headerType={headerType}
            sidebar={sidebar}
            sidebarEnabled={sidebarEnabled}
            leftNav={leftNav}
            useH1ForTitle
          />
          <DebugPageSections
            rawSectionData={configuration}
            resolvedSectionData={newBanner.bannerTemplate}
          />
        </>
      )
    } else {
      return <></>
    }
  }

  const backgroungVariant = sidebarEnabled ? 'card' : fullWidthImage ? 'full-width-image' : 'card'

  return (
    <BoundedContent paddingX={0} marginBottom={{ xs: 7, sm: 7, md: 8 }}>
      <Banner
        imageFocalPoint={imageFocalPoint ?? 'right bottom'}
        withLeftHandNavigation={!!sidebarEnabled}
        textBoxHorizontalAlign="left"
        style={{ '--swan-public-marketing-background': color?.code } as React.CSSProperties}
        darkMode={color?.mode.dark}
        mb={marginBottom}
        variant={backgroungVariant}
        className={classNames({
          'full-height-carousel-content': isCarousel
        })}
      >
        {sidebarEnabled && sidebar && leftNav}
        <StandardHeroContent type={headerType} {...configurationAsHeroContent} isH1={isH1} />
      </Banner>
      <DebugPageSections
        rawSectionData={configuration}
        resolvedSectionData={configurationAsHeroContent}
      />
    </BoundedContent>
  )
}

export const shouldBeH1 = (headerType: string, isCarousel?: boolean, index?: number) => {
  if (headerType === 'page header' || headerType === 'sub-category page header') {
    if (!isCarousel) {
      return true
    }

    if (isCarousel && index === 0) {
      return true
    }
  }
  return false
}

export const getBackgroundColor = (props: {
  defaultColor: string
  overrideColor?: string
  heroType: string
}) => {
  const { overrideColor, defaultColor, heroType } = props

  if (overrideColor && heroType !== 'page header') {
    return overrideColor
  }

  return defaultColor
}

export const getResponsiveBannerImages = (
  locale: string
): { breakpoint: number; src: string }[] => {
  const breakpoints = [
    { width: 384, breakpoint: 768 },
    { width: 512, breakpoint: 1024 },
    { width: 720, breakpoint: 1440 },
    { width: 960, breakpoint: 1920 },
    { width: 1280, breakpoint: 2560 },
    { width: 1440, breakpoint: 2880 },
    { width: 1920, breakpoint: 3840 }
  ]
  const imagePrefix = 'https://cms.cloudinary.vpsvc.com/image/upload/f_auto,q_auto,w_'
  const bannerImageByLocale: Record<string, string> = {
    'en-US': 'NPIB-64883-BC-Category-Page-experiment-001_en-us_xqp8ij.png',
    'es-US': 'NPIB-64883-BC-Category-Page-experiment-002_es-us_cfro38.png',
    'en-GB': 'MXP-36240-BannerFullWidth-Unmatched-001_en-gb_fqjevh.png'
  }
  const imagePath: string = bannerImageByLocale[locale]
  return breakpoints.map(({ width, breakpoint }) => ({
    breakpoint,
    src: `${imagePrefix}${width}/${imagePath}`
  }))
}

export default PageHeader
